import { Play, Pause, Square, Volume2, VolumeX } from "lucide-react";
import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";

interface VideoHeroProps {
  videoSrc: string;
  title: string;
  description: string;
}

export const VideoHero = ({ videoSrc, title, description }: VideoHeroProps) => {
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const stopVideo = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
      console.log("Mute toggled:", !isMuted);
    }
  };

  const handleVideoLoad = () => {
    console.log("Video loaded successfully");
    if (videoRef.current) {
      videoRef.current.play().catch(error => {
        console.error("Autoplay failed:", error);
        setIsPlaying(false);
      });
    }
  };

  const handleVideoError = (error: any) => {
    console.error("Video error:", error);
  };

  return (
    <div className="space-y-4">
      <div className="relative w-full h-[80vh] min-h-[600px] overflow-hidden rounded-xl bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 shadow-2xl border border-slate-700/50 flex items-center justify-center">
        {/* Ambient glow effect */}
        <div className="absolute inset-0 bg-gradient-to-t from-blue-900/20 via-transparent to-purple-900/10 rounded-xl"></div>
        
        <video
          ref={videoRef}
          className="h-[90%] object-contain shadow-lg rounded-lg z-10"
          src={videoSrc}
          autoPlay
          muted
          playsInline
          loop
          controls={false}
          onLoadedData={handleVideoLoad}
          onError={handleVideoError}
          onPlay={() => {
            console.log("Video started playing");
            setIsPlaying(true);
          }}
          onPause={() => {
            console.log("Video paused");
            setIsPlaying(false);
          }}
          onEnded={() => {
            console.log("Video ended");
            setIsPlaying(false);
          }}
          onCanPlay={() => {
            console.log("Video can play");
          }}
        />
      </div>
      
      {/* Video Controls - Outside the video */}
      <div className="flex justify-center gap-3">
        <Button
          onClick={togglePlay}
          size="default"
          variant="ghost"
          className="gap-2 bg-slate-800/80 hover:bg-slate-700/90 text-slate-200 border border-slate-600/50 backdrop-blur-sm transition-all duration-300"
        >
          {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
          {isPlaying ? "Pause" : "Play"}
        </Button>
        <Button
          onClick={stopVideo}
          size="default"
          variant="ghost"
          className="gap-2 bg-slate-800/80 hover:bg-slate-700/90 text-slate-200 border border-slate-600/50 backdrop-blur-sm transition-all duration-300"
        >
          <Square className="h-4 w-4" />
          Stop
        </Button>
        <Button
          onClick={toggleMute}
          size="default"
          variant="ghost"
          className="gap-2 bg-slate-800/80 hover:bg-slate-700/90 text-slate-200 border border-slate-600/50 backdrop-blur-sm transition-all duration-300"
        >
          {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          {isMuted ? "Unmute" : "Mute"}
        </Button>
      </div>
    </div>
  );
};